import type { FC } from 'react';
import { useParams, Navigate } from 'react-router';
import type { NetworkPointDetail } from 'src/apis/monolith/types';
import { PageSpinner } from 'src/components/spinner-container';
import { useNetworkPointByUuid } from 'src/hooks/use-network-point-by-uuid';
import { stringifySearchParams } from 'src/hooks/use-page-params-from-schema';
import type { BalancingGroupSuggestion } from 'src/pages/reporting/balancing-group-allocations/balancing-group-searchable-multi-select';
import type { PageParams } from 'src/pages/reporting/balancing-group-allocations/use-page-params';

type BgaRouteParams = {
  nwpUuid: string;
  balancingGroupId: string;
  balancingGroupName: string;
  allocationIntervalIntersectionStart: string;
  allocationIntervalIntersectionEnd: string;
};

type BgaSearchParams = Pick<
  PageParams['filterValues'],
  | 'networkPointId'
  | 'balancingGroupId'
  | 'allocationIntervalIntersectionFilterStart'
  | 'allocationIntervalIntersectionFilterEnd'
>;

const bgaRedirectSearchString = (
  params: BgaRouteParams,
  nwpResponse: NetworkPointDetail
) => {
  const { name, marketArea0, tso0ShortName, direction } = nwpResponse;
  const {
    balancingGroupId,
    balancingGroupName,
    nwpUuid: networkPointUuid,
    allocationIntervalIntersectionStart,
    allocationIntervalIntersectionEnd,
  } = params;

  const directionMeta = {
    marketArea: marketArea0,
    tsoShortName: tso0ShortName,
  };

  // This redirect is always issued per network point, never for a bundle, so
  // `direction` is only ever ENTRY or EXIT here.
  const networkPointData: BgaSearchParams['networkPointId'][number] =
    direction === 'ENTRY'
      ? { value: networkPointUuid, label: name, entry: directionMeta }
      : { value: networkPointUuid, label: name, exit: directionMeta };

  const balancingGroupData: BalancingGroupSuggestion = {
    value: balancingGroupId,
    label: balancingGroupName,
  };

  const search = {
    networkPointId: [networkPointData],
    balancingGroupId: [balancingGroupData],
    allocationIntervalIntersectionFilterStart:
      allocationIntervalIntersectionStart,
    allocationIntervalIntersectionFilterEnd: allocationIntervalIntersectionEnd,
  } satisfies BgaSearchParams;

  return stringifySearchParams(search);
};

export const BgaRedirect: FC = () => {
  const params = useParams<BgaRouteParams>();

  const networkPointRequest = useNetworkPointByUuid({ uuid: params.nwpUuid });

  if (networkPointRequest.error)
    return <Navigate replace to="/reporting/balancing-group-allocations" />;

  if (!networkPointRequest.response) return <PageSpinner />;

  return (
    <Navigate
      replace
      to={{
        pathname: '/reporting/balancing-group-allocations',
        search: bgaRedirectSearchString(
          params,
          networkPointRequest.response.data
        ),
      }}
    />
  );
};
